import { catchError, concatMap, map, of, tap } from 'rxjs';
import i18next from 'i18next';
import { CrudServiceBase, RxUtils, TypeUtils } from '@utils';
import { httpService } from '@services/http';
import { logger } from '@services/logger';
import { notificationService } from '@services/notification';
import { Product } from './product.class';
import { ChargerCurrentType } from './charger-current-type.enum';
import { ChargerUsingType } from './charger-using-type.enum';

type Entity = Product;

export class ProductService extends CrudServiceBase<Entity> {
	constructor() {
		super(Product, 'product');
	}

	create(entity: Partial<Entity>, images?: File[]) {
		return super.create(entity).pipe(concatMap((result) => this.updateImages(result, images || [])));
	}

	update(id: string, entity: Partial<Entity>, images?: File[]) {
		return super.update(id, entity).pipe(concatMap((result) => this.updateImages(result, images || [])));
	}

	updateImages(entity: Entity, images: File[]) {
		const formData = new FormData();
		images.map((image) => formData.append('images', image, image.name));
		return httpService
			.post<unknown>(this.getUrl(), `${entity.id}/images`, { body: formData, headers: { 'Content-Type': 'multipart/form-data' } })
			.pipe(
				tap((result) => logger.debug(`[${this.constructor.name}.${this.updateImages.name}]`, result)),
				map(() => entity),
				catchError(() => {
					notificationService.sendError(i18next.t('common.files-upload-failed'));
					return of(entity);
				})
			);
	}

	getImages(entity: Entity) {
		return httpService.get<string[]>(this.getUrl(), `${entity.id}/images`).pipe(
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getImages.name}]`, result)),
			catchError(() => of([] as string[]))
		);
	}

	getImageForChargePointType(currentType: ChargerCurrentType, usage?: ChargerUsingType) {
		return httpService.get<Entity[]>(this.getUrl(), `charge-point/${currentType}`, { params: { usage } }).pipe(
			concatMap((result) => RxUtils.mapValueOrThrow(Array.isArray(result) && result.length, new Error('Response is empty'), result)),
			map((entity) => TypeUtils.convert(Product, entity)),
			tap((result) => logger.debug(`[${this.constructor.name}.${this.getImageForChargePointType.name}.product]`, result)),
			concatMap((product) => this.getImages(product[0])),
			tap((images) => logger.debug(`[${this.constructor.name}.${this.getImageForChargePointType.name}.images]`, images)),
			catchError(() => of([''])),
			map((images) => images[0])
		);
	}
}

export const productService = new ProductService();
